import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import NewsCard from '../components/UI/NewsCard';

interface NewsArticle {
  id: string;
  title: string;
  summary: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: {
    impact: number;
    reach: number;
    duration: number;
  };
  sourceName: string;
  publishedAt: string;
}

const CategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  
  const { data: newsData, isLoading, error } = useQuery({
    queryKey: ['category-news', category],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/news/category/${encodeURIComponent(category || '')}`);
      return response.data;
    },
    enabled: !!category
  });

  const getCategoryInfo = (cat: string) => {
    const categories: Record<string, { icon: string; description: string; fullName: string }> = {
      'Geopolitics': {
        icon: '🌍',
        fullName: 'Geopolitics & International Politics',
        description: 'Global political developments, diplomatic relations, and international conflicts affecting multiple nations.'
      },
      'Economy': {
        icon: '💰',
        fullName: 'Global Economy & Finance',
        description: 'Economic developments, market movements, and financial policies with worldwide implications.'
      },
      'Climate': {
        icon: '🌱',
        fullName: 'Environment & Climate',
        description: 'Environmental changes, climate policies, and sustainability initiatives affecting our planet.'
      },
      'Health': {
        icon: '🧬',
        fullName: 'Global Health',
        description: 'Health crises, medical breakthroughs, and public health policies affecting large populations.'
      },
      'Technology': {
        icon: '🤖',
        fullName: 'Technology & Applied Science',
        description: 'Technological advances and scientific discoveries with significant societal implications.'
      },
      'Disasters': {
        icon: '🚨',
        fullName: 'Disasters & Accidents',
        description: 'Major disasters, accidents, and emergencies with significant impact on communities.'
      },
      'Migration': {
        icon: '👥',
        fullName: 'Demographics & Migration',
        description: 'Population movements, demographic changes, and migration policies affecting societies.'
      }
    };
    
    return categories[cat] || { icon: '📰', fullName: cat || '', description: 'News articles' };
  };

  const categoryInfo = getCategoryInfo(category || '');
  const articles: NewsArticle[] = newsData?.data || [];

  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">
            Unable to load category news
          </h2>
          <p className="text-primary-600">
            Please try again later.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      {/* Category Header */}
      <section className="bg-primary-50 border-b border-primary-200">
        <div className="container-news py-16">
          <div className="text-center">
            <div className="text-6xl mb-4">{categoryInfo.icon}</div>
            <h1 className="headline-main mb-6">
              {categoryInfo.fullName}
            </h1>
            <p className="text-xl text-primary-700 max-w-3xl mx-auto leading-relaxed">
              {categoryInfo.description}
            </p>
            <div className="mt-8 flex items-center justify-center space-x-8 text-primary-600">
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">
                  {articles.length}
                </div>
                <div className="text-sm">Articles</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">
                  {articles.reduce((sum, article) => sum + article.noticiabilityTotal, 0).toFixed(0)}
                </div>
                <div className="text-sm">Total Impact</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent-600">
                  {articles.length > 0 ? (articles.reduce((sum, article) => sum + article.biasScore, 0) / articles.length).toFixed(1) : '0.0'}
                </div>
                <div className="text-sm">Avg Bias Score</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="container-news py-12">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="news-card">
                <div className="loading-skeleton h-48 mb-4"></div>
                <div className="p-6">
                  <div className="loading-skeleton h-4 mb-2"></div>
                  <div className="loading-skeleton h-4 w-3/4 mb-4"></div>
                  <div className="loading-skeleton h-3 mb-2"></div>
                  <div className="loading-skeleton h-3 w-5/6"></div>
                </div>
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-6xl mb-6">{categoryInfo.icon}</div>
            <h3 className="font-playfair text-2xl font-bold text-primary-900 mb-4">
              No articles yet in {categoryInfo.fullName}
            </h3>
            <p className="text-primary-600 max-w-md mx-auto">
              Check back later for high-impact news in this category. Our AI is continuously 
              monitoring global sources for relevant developments.
            </p>
          </div>
        ) : (
          <>
            {/* High Impact Stories */}
            {articles.filter(article => article.noticiabilityTotal > 400).length > 0 && (
              <div className="mb-12">
                <h2 className="headline-section mb-8">High Impact Stories</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {articles
                    .filter(article => article.noticiabilityTotal > 400)
                    .slice(0, 2)
                    .map(article => (
                      <NewsCard key={article.id} article={article} featured={true} />
                    ))}
                </div>
              </div>
            )}

            {/* All Articles */}
            <div className="mb-12">
              <h2 className="headline-section mb-8">
                {articles.filter(article => article.noticiabilityTotal > 400).length > 0 
                  ? 'More Stories' 
                  : 'Latest Stories'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles
                  .filter(article => article.noticiabilityTotal <= 400 || articles.filter(a => a.noticiabilityTotal > 400).length === 0)
                  .map(article => (
                    <NewsCard key={article.id} article={article} />
                  ))}
              </div>
            </div>

            {/* Category Stats */}
            <div className="bg-primary-50 newspaper-border p-8">
              <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">
                {categoryInfo.fullName} Insights
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">
                    {articles.filter(a => a.noticiabilityTotal > 400).length}
                  </div>
                  <div className="text-primary-700">High Impact Stories</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">
                    {articles.filter(a => a.biasScore <= 3).length}
                  </div>
                  <div className="text-primary-700">Low Bias Articles</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-accent-600 mb-2">
                    {new Set(articles.map(a => a.sourceName)).size}
                  </div>
                  <div className="text-primary-700">Unique Sources</div>
                </div>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
};

export default CategoryPage;